import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, eur } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Empty, ErrorBox, Field, Loading, Modal, PageHead, Tabs, useAction, useLoad } from '../components/ui';
import { CustomTemplates, DeliverySettings } from './Delivery';

type TabId = 'empresa' | 'conexiones' | 'entrega' | 'cuentas' | 'aprobacion' | 'usuarios' | 'seguridad' | 'plan' | 'empresas' | 'api';

export function Settings() {
  const s = useSession();
  const [params] = useSearchParams();
  const mustEnroll = Boolean(s.me?.mfaEnrollmentRequired);
  // ?tab=entrega: vuelta de la conexión con Google o Microsoft
  const [tab, setTab] = useState<TabId>(mustEnroll || !s.can('settings.manage') ? 'seguridad' : params.get('tab') === 'entrega' ? 'entrega' : 'empresa');
  const items: Array<{ id: TabId; label: string }> = [
    ...(s.can('settings.manage') ? [{ id: 'empresa' as const, label: 'Empresa' }, { id: 'conexiones' as const, label: 'Conexiones' }, { id: 'entrega' as const, label: 'Entrega y conservación' }, { id: 'cuentas' as const, label: 'Cuentas y centros de coste' }, { id: 'aprobacion' as const, label: 'Aprobaciones' }] : []),
    { id: 'usuarios', label: 'Usuarios' },
    { id: 'seguridad', label: 'Mi seguridad' },
    ...(s.can('settings.manage') ? [{ id: 'plan' as const, label: 'Plan y uso' }, { id: 'empresas' as const, label: 'Empresas' }, { id: 'api' as const, label: 'API y webhooks' }] : []),
  ];
  // En la prueba no hay conexiones, entrega, usuarios, plan ni API
  const visibles = s.prueba ? items.filter((i) => !['conexiones', 'entrega', 'usuarios', 'plan', 'api'].includes(i.id)) : items;
  return (
    <>
      <PageHead title="Ajustes" />
      {mustEnroll && (
        <div className="alert warn" style={{ marginBottom: 12 }}>
          Tu rol exige el segundo factor (2FA). Actívalo en «Mi seguridad» para seguir usando Conforme.
        </div>
      )}
      {params.get('conectado') && <div className="alert ok" style={{ marginBottom: 12 }}>Cuenta conectada. Revisa la carpeta de destino y guarda la entrega.</div>}
      {params.get('conexion') === 'error' && <div className="alert warn" style={{ marginBottom: 12 }}>No se pudo conectar la cuenta. Inténtalo de nuevo.</div>}
      {params.get('bienvenida') && (
        <div className="alert ok" style={{ marginBottom: 12 }}>
          ¡Bienvenido! Para empezar: 1) revisa los datos contables de tu empresa, 2) importa tu plan de cuentas y tus proveedores, 3) reenvía tus facturas a la dirección de correo de la empresa o súbelas desde «Subir facturas».
        </div>
      )}
      <Tabs value={tab} onChange={setTab} items={visibles} />
      {['empresa', 'conexiones', 'entrega', 'cuentas', 'aprobacion'].includes(tab) && !s.companyId ? <Empty>Elige una empresa arriba.</Empty> : (
        <>
          {tab === 'empresa' && <CompanySettings />}
          {tab === 'conexiones' && <><Connections /><CustomTemplates /></>}
          {tab === 'entrega' && <DeliverySettings />}
          {tab === 'cuentas' && <Accounts />}
          {tab === 'aprobacion' && <ApprovalRules />}
          {tab === 'usuarios' && <Users />}
          {tab === 'seguridad' && <Security />}
          {tab === 'plan' && <Plan />}
          {tab === 'empresas' && <Companies />}
          {tab === 'api' && <ApiAccess />}
        </>
      )}
    </>
  );
}

function useCompany() {
  const s = useSession();
  return useLoad(async () => (await api.get('/companies')).find((c: any) => c.id === s.companyId), [s.companyId]);
}

function CompanySettings() {
  const s = useSession();
  const { data: c, reload } = useCompany();
  const { busy, run } = useAction();
  const [f, setF] = useState<any>(null);
  useEffect(() => { if (c) setF({ name: c.name, taxRegime: c.tax_regime, accountingSystem: c.accounting_system, annualTurnoverOver8m: c.annual_turnover_over_8m, inboxAlias: c.inbox_alias, ...c.settings }); }, [c]);
  if (!f) return <Loading />;
  const set = (k: string, v: unknown) => setF({ ...f, [k]: v });
  const save = async () => {
    const { name, taxRegime, accountingSystem, annualTurnoverOver8m, inboxAlias, holded, odoo, ...settings } = f;
    const numeric = ['autoApproveMaxAmount', 'accountLength', 'simplifiedLimit', 'baselineMinutesPerInvoice', 'hourlyCostEur'];
    for (const k of numeric) if (settings[k] !== undefined && settings[k] !== '') settings[k] = Number(settings[k]);
    for (const k of ['debtorIban', 'debtorBic', 'debtorName', 'sepaSuffix']) if (settings[k] === '') settings[k] = null;
    if (await run(() => api.patch(`/companies/${c.id}`, { name, taxRegime, accountingSystem, annualTurnoverOver8m, inboxAlias, settings }), 'Ajustes guardados')) { reload(); s.refresh(); }
  };
  return (
    <div className="stack">
      <div className="card stack">
        <h2>Datos de la empresa</h2>
        <div className="grid3">
          <Field label="Razón social"><input value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
          <Field label="NIF"><input disabled value={c.nif} /></Field>
          <Field label="Territorio fiscal"><select value={f.taxRegime} onChange={(e) => set('taxRegime', e.target.value)}><option value="IVA">Península y Baleares (IVA)</option><option value="IGIC">Canarias (IGIC)</option><option value="IPSI">Ceuta y Melilla (IPSI)</option></select></Field>
        </div>
        <Field label="Dirección de entrada de facturas" hint={`Dirección completa: ${f.inboxAlias}@${c.inboxAddress.split('@')[1]}. Pide a tus proveedores que envíen aquí las facturas, o reenvíalas tú. Letras minúsculas, números y guiones. Si la cambias, la anterior sigue funcionando.`}>
          <div className="row"><input className="mono" value={f.inboxAlias ?? ''} onChange={(e) => set('inboxAlias', e.target.value.toLowerCase())} style={{ maxWidth: 260 }} /><span className="muted">@{c.inboxAddress.split('@')[1]}</span></div>
        </Field>
        <label className="check"><input type="checkbox" checked={Boolean(f.annualTurnoverOver8m)} onChange={(e) => set('annualTurnoverOver8m', e.target.checked)} /> La empresa factura más de 8 millones de € al año (calendario de la factura electrónica obligatoria)</label>
      </div>
      <div className="card stack">
        <h2>Contabilidad</h2>
        <div className="grid3">
          <Field label="Programa contable"><select value={f.accountingSystem} onChange={(e) => set('accountingSystem', e.target.value)}>{['a3', 'sage', 'contasol', 'holded', 'odoo', 'otro', 'csv'].map((x) => <option key={x} value={x}>{({ a3: 'a3 (Wolters Kluwer)', sage: 'Sage', contasol: 'ContaSOL', holded: 'Holded', odoo: 'Odoo', otro: 'Otro', csv: 'Sin definir' } as any)[x]}</option>)}</select></Field>
          <Field label="Formato de exportación por defecto"><input value={f.exportTarget ?? ''} onChange={(e) => set('exportTarget', e.target.value)} /></Field>
          <Field label="Cuenta de gasto por defecto"><input className="mono" value={f.expenseDefaultAccount ?? ''} onChange={(e) => set('expenseDefaultAccount', e.target.value)} /></Field>
          <Field label="Prefijo de subcuentas de proveedor"><input className="mono" value={f.vendorAccountPrefix ?? ''} onChange={(e) => set('vendorAccountPrefix', e.target.value)} /></Field>
          <Field label="Longitud de las subcuentas"><input type="number" value={f.accountLength ?? 8} onChange={(e) => set('accountLength', e.target.value)} /></Field>
          <Field label="Cuenta de retenciones IRPF"><input className="mono" value={f.withholdingAccount ?? ''} onChange={(e) => set('withholdingAccount', e.target.value)} /></Field>
        </div>
        <label className="check"><input type="checkbox" checked={Boolean(f.subaccountsByRate)} onChange={(e) => set('subaccountsByRate', e.target.checked)} /> Usar subcuentas de IVA soportado por tipo (p. ej. 47200021)</label>
      </div>
      <div className="card stack">
        <h2>Automatización y control</h2>
        <label className="check"><input type="checkbox" checked={Boolean(f.autoApproveEnabled)} onChange={(e) => set('autoApproveEnabled', e.target.checked)} /> Aprobar sin intervención las facturas sin ninguna incidencia de proveedores conocidos</label>
        <div className="grid3">
          <Field label="Importe máximo para aprobar sin intervención (€)"><input type="number" value={f.autoApproveMaxAmount ?? 0} onChange={(e) => set('autoApproveMaxAmount', e.target.value)} /></Field>
          <Field label="Límite de factura simplificada (€)"><input type="number" value={f.simplifiedLimit ?? 400} onChange={(e) => set('simplifiedLimit', e.target.value)} /></Field>
        </div>
        <label className="check"><input type="checkbox" checked={Boolean(f.segregateUploadApprove)} onChange={(e) => set('segregateUploadApprove', e.target.checked)} /> Quien sube una factura no puede aprobarla</label>
        <label className="check"><input type="checkbox" checked={f.strictPaymentSegregation !== false} onChange={(e) => set('strictPaymentSegregation', e.target.checked)} /> Segregación de funciones en pagos (prepara ≠ aprueba ≠ descarga)</label>
      </div>
      {s.hasModule('pagos') && (
        <div className="card stack">
          <h2>Pagos</h2>
          <div className="grid2">
            <Field label="IBAN de cargo"><input className="mono" value={f.debtorIban ?? ''} onChange={(e) => set('debtorIban', e.target.value)} /></Field>
            <Field label="Titular"><input value={f.debtorName ?? ''} onChange={(e) => set('debtorName', e.target.value)} /></Field>
            <Field label="BIC (opcional)"><input className="mono" value={f.debtorBic ?? ''} onChange={(e) => set('debtorBic', e.target.value)} /></Field>
            <Field label="Sufijo del ordenante (lo da tu banco)"><input className="mono" maxLength={3} value={f.sepaSuffix ?? ''} onChange={(e) => set('sepaSuffix', e.target.value)} /></Field>
          </div>
        </div>
      )}
      <div className="card stack">
        <h2>Cálculo del ahorro</h2>
        <div className="grid2">
          <Field label="Minutos por factura antes de Conforme"><input type="number" value={f.baselineMinutesPerInvoice ?? 4} onChange={(e) => set('baselineMinutesPerInvoice', e.target.value)} /></Field>
          <Field label="Coste por hora de administración (€)"><input type="number" value={f.hourlyCostEur ?? 20} onChange={(e) => set('hourlyCostEur', e.target.value)} /></Field>
        </div>
      </div>
      <div><button className="primary" disabled={busy} onClick={save}>Guardar ajustes</button></div>
    </div>
  );
}

function Connections() {
  const { data: c, reload } = useCompany();
  const { busy, run } = useAction();
  const [holded, setHolded] = useState<any>({ apiKey: '' });
  const [odoo, setOdoo] = useState<any>({ url: '', db: '', username: '', password: '' });
  useEffect(() => { if (c) { setHolded(c.settings?.holded ?? { apiKey: '' }); setOdoo(c.settings?.odoo ?? { url: '', db: '', username: '', password: '' }); } }, [c]);
  if (!c) return <Loading />;
  return (
    <div className="grid2">
      <div className="card stack">
        <h2>Holded</h2>
        <p className="hint">Envía las facturas aprobadas como documentos de compra. Prueba primero con una factura.</p>
        <Field label="Clave de API"><input type="password" value={holded.apiKey} onChange={(e) => setHolded({ ...holded, apiKey: e.target.value })} /></Field>
        <Field label="Ruta de la API (avanzado)"><input value={holded.purchasePath ?? '/invoicing/v1/documents/purchase'} onChange={(e) => setHolded({ ...holded, purchasePath: e.target.value })} /></Field>
        <div className="row"><button disabled={busy} onClick={async () => { if (await run(() => api.patch(`/companies/${c.id}`, { settings: { holded: holded.apiKey ? holded : null } }), 'Guardado')) reload(); }}>Guardar</button></div>
      </div>
      <div className="card stack">
        <h2>Odoo</h2>
        <p className="hint">Crea facturas de proveedor (account.move) en tu Odoo.</p>
        <Field label="URL"><input value={odoo.url} placeholder="https://miempresa.odoo.com" onChange={(e) => setOdoo({ ...odoo, url: e.target.value })} /></Field>
        <div className="grid2">
          <Field label="Base de datos"><input value={odoo.db} onChange={(e) => setOdoo({ ...odoo, db: e.target.value })} /></Field>
          <Field label="Usuario"><input value={odoo.username} onChange={(e) => setOdoo({ ...odoo, username: e.target.value })} /></Field>
        </div>
        <Field label="Contraseña o clave de API"><input type="password" value={odoo.password} onChange={(e) => setOdoo({ ...odoo, password: e.target.value })} /></Field>
        <div className="row"><button disabled={busy} onClick={async () => { if (await run(() => api.patch(`/companies/${c.id}`, { settings: { odoo: odoo.url ? odoo : null } }), 'Guardado')) reload(); }}>Guardar</button></div>
      </div>
    </div>
  );
}

function Accounts() {
  const s = useSession();
  const { busy, run } = useAction();
  const accounts = useLoad(() => api.get(`/companies/${s.companyId}/accounts`), [s.companyId]);
  const ccs = useLoad(() => api.get(`/companies/${s.companyId}/cost-centers`), [s.companyId]);
  const users = useLoad(() => api.get('/users'), []);
  const [csv, setCsv] = useState('');
  const [list, setList] = useState<any[]>([]);
  useEffect(() => { setList((ccs.data ?? []).map((c: any) => ({ code: c.code, name: c.name, approverUserId: c.approver_user_id }))); }, [ccs.data]);
  return (
    <div className="grid2">
      <div className="card stack">
        <h2>Plan de cuentas <Badge>{(accounts.data ?? []).length}</Badge></h2>
        <p className="hint">Pega o sube un CSV con <code>cuenta;nombre</code> exportado de tu programa contable. Sirve para sugerir y validar cuentas.</p>
        <input type="file" accept=".csv,.txt" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); }} />
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={'cuenta;nombre\n600;Compras de mercaderías\n628;Suministros'} />
        <div><button disabled={busy || !csv} onClick={async () => { const r = await run(() => api.post(`/companies/${s.companyId}/accounts/import`, { csv })); if (r) { setCsv(''); accounts.reload(); alert(`${r.imported} cuentas importadas`); } }}>Importar</button></div>
        <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}><table><tbody>{(accounts.data ?? []).map((a: any) => <tr key={a.code}><td className="mono">{a.code}</td><td>{a.name}</td></tr>)}</tbody></table></div>
      </div>
      <div className="card stack">
        <h2>Centros de coste</h2>
        <p className="hint">Si un centro tiene responsable, sus facturas le llegan a él para aprobar.</p>
        {list.map((c, i) => (
          <div key={i} className="row">
            <input style={{ width: 90 }} placeholder="Código" value={c.code} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)))} />
            <input placeholder="Nombre" value={c.name} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <select value={c.approverUserId ?? ''} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, approverUserId: e.target.value || null } : x)))}>
              <option value="">Sin responsable</option>{(users.data ?? []).map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <button className="ghost small" aria-label="Quitar" onClick={() => setList(list.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <div className="row"><button className="small" onClick={() => setList([...list, { code: '', name: '', approverUserId: null }])}>+ Añadir</button>
          <button className="primary" disabled={busy} onClick={async () => { if (await run(() => api.put(`/companies/${s.companyId}/cost-centers`, list.filter((x) => x.code && x.name)), 'Centros de coste guardados')) ccs.reload(); }}>Guardar</button></div>
      </div>
    </div>
  );
}

function ApprovalRules() {
  const s = useSession();
  const { busy, run } = useAction();
  const rules = useLoad(() => api.get(`/approval-rules?companyId=${s.companyId}`), [s.companyId]);
  const users = useLoad(() => api.get('/users'), []);
  const [f, setF] = useState<any>(null);
  const label = (l: any) => l.userId ? (users.data ?? []).find((u: any) => u.id === l.userId)?.name ?? 'usuario' : `cualquier ${l.role}`;
  return (
    <div className="stack">
      <div className="alert info">Sin reglas, las facturas las aprueba cualquier usuario con rol Aprobador o Administrador. Con reglas, se aplica la primera que encaje (por prioridad).</div>
      {(rules.data ?? []).length === 0 ? <Empty>Sin reglas de aprobación.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Prioridad</th><th>Nombre</th><th>Condiciones</th><th>Niveles</th><th /></tr></thead>
          <tbody>{rules.data.map((r: any) => (
            <tr key={r.id}><td>{r.priority}</td><td>{r.name}</td>
              <td>{[r.min_amount !== null && `desde ${eur(r.min_amount)}`, r.max_amount !== null && `hasta ${eur(r.max_amount)}`, r.cost_center && `centro ${r.cost_center}`].filter(Boolean).join(', ') || 'Todas'}</td>
              <td>{r.levels.map((l: any, i: number) => <div key={i}>{i + 1}. {label(l)}</div>)}</td>
              <td><button className="small ghost" onClick={async () => { if (confirm('¿Eliminar la regla?') && await run(() => api.del(`/approval-rules/${r.id}`))) rules.reload(); }}>Eliminar</button></td></tr>
          ))}</tbody>
        </table></div>
      )}
      <div><button onClick={() => setF({ name: '', priority: 100, minAmount: '', maxAmount: '', costCenter: '', levels: [{ role: 'aprobador', userId: null }] })}>Nueva regla</button></div>
      {f && (
        <Modal title="Nueva regla de aprobación" onClose={() => setF(null)} footer={<button className="primary" disabled={busy || !f.name} onClick={async () => {
          const body = { companyId: s.companyId, name: f.name, priority: Number(f.priority), minAmount: f.minAmount === '' ? null : Number(f.minAmount), maxAmount: f.maxAmount === '' ? null : Number(f.maxAmount), costCenter: f.costCenter || null, levels: f.levels.map((l: any) => (l.userId ? { userId: l.userId } : { role: l.role })) };
          if (await run(() => api.post('/approval-rules', body), 'Regla creada')) { setF(null); rules.reload(); }
        }}>Crear</button>}>
          <div className="grid2">
            <Field label="Nombre"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Facturas de más de 3.000 €" /></Field>
            <Field label="Prioridad (menor = antes)"><input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} /></Field>
            <Field label="Importe desde (€)"><input type="number" value={f.minAmount} onChange={(e) => setF({ ...f, minAmount: e.target.value })} /></Field>
            <Field label="Importe hasta (€)"><input type="number" value={f.maxAmount} onChange={(e) => setF({ ...f, maxAmount: e.target.value })} /></Field>
            <Field label="Centro de coste (opcional)"><input value={f.costCenter} onChange={(e) => setF({ ...f, costCenter: e.target.value })} /></Field>
          </div>
          <h3>Niveles</h3>
          {f.levels.map((l: any, i: number) => (
            <div key={i} className="row">
              <span>{i + 1}.</span>
              <select value={l.userId ?? `role:${l.role}`} onChange={(e) => { const v = e.target.value; const lv = v.startsWith('role:') ? { role: v.slice(5), userId: null } : { userId: v, role: null }; setF({ ...f, levels: f.levels.map((x: any, j: number) => (j === i ? lv : x)) }); }}>
                <option value="role:aprobador">Cualquier aprobador</option><option value="role:admin">Cualquier administrador</option><option value="role:tesoreria">Cualquier persona de tesorería</option>
                {(users.data ?? []).map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {f.levels.length > 1 && <button className="ghost small" onClick={() => setF({ ...f, levels: f.levels.filter((_: any, j: number) => j !== i) })}>✕</button>}
            </div>
          ))}
          {f.levels.length < 5 && <div><button className="small" onClick={() => setF({ ...f, levels: [...f.levels, { role: 'admin', userId: null }] })}>+ Añadir nivel</button></div>}
        </Modal>
      )}
    </div>
  );
}

const ROLES = [['admin', 'Administrador'], ['contable', 'Contabilidad / administración'], ['aprobador', 'Aprobador'], ['tesoreria', 'Tesorería'], ['consulta', 'Solo consulta'], ['asesor', 'Asesoría externa']] as const;

function Users() {
  const s = useSession();
  const { busy, run } = useAction();
  const { data, reload } = useLoad(() => api.get('/users'), []);
  const [f, setF] = useState<any>(null);
  return (
    <div className="stack">
      <div className="table-wrap"><table>
        <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Empresas</th><th>2FA</th><th>Último acceso</th><th /></tr></thead>
        <tbody>{(data ?? []).map((u: any) => (
          <tr key={u.membership_id}>
            <td>{u.name}</td><td>{u.email}</td>
            <td>{s.can('users.manage') && u.id !== s.me?.user.id ? <select value={u.role} onChange={async (e) => { if (await run(() => api.patch(`/users/${u.membership_id}`, { role: e.target.value }), 'Rol actualizado')) reload(); }}>{ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select> : u.roleLabel}</td>
            <td>{u.company_ids ? `${u.company_ids.length} empresa(s)` : 'Todas'}</td>
            <td>{u.mfa_enabled ? <Badge tone="ok">Activo</Badge> : <Badge tone="warn">No</Badge>}</td>
            <td>{dateTime(u.last_login_at)}</td>
            <td>{s.can('users.manage') && u.id !== s.me?.user.id && <button className="small ghost" onClick={async () => { if (confirm(`¿Quitar el acceso a ${u.name}?`) && await run(() => api.del(`/users/${u.membership_id}`), 'Acceso retirado')) reload(); }}>Quitar</button>}</td>
          </tr>
        ))}</tbody>
      </table></div>
      {s.can('users.manage') && <div><button onClick={() => setF({ name: '', email: '', role: 'aprobador', all: true, companyIds: [] })}>Invitar usuario</button></div>}
      {f && (
        <Modal title="Invitar usuario" onClose={() => setF(null)} footer={<button className="primary" disabled={busy || !f.email || !f.name} onClick={async () => {
          if (await run(() => api.post('/users', { name: f.name, email: f.email, role: f.role, companyIds: f.all ? null : f.companyIds }), 'Invitación enviada por correo')) { setF(null); reload(); }
        }}>Invitar</button>}>
          <Field label="Nombre"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Correo"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Rol"><select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>{ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
          {(s.me?.companies.length ?? 0) > 1 && (
            <>
              <label className="check"><input type="checkbox" checked={f.all} onChange={(e) => setF({ ...f, all: e.target.checked })} /> Acceso a todas las empresas</label>
              {!f.all && s.me!.companies.map((c) => <label key={c.id} className="check"><input type="checkbox" checked={f.companyIds.includes(c.id)} onChange={(e) => setF({ ...f, companyIds: e.target.checked ? [...f.companyIds, c.id] : f.companyIds.filter((x: string) => x !== c.id) })} /> {c.name}</label>)}
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function Security() {
  const s = useSession();
  const { busy, run } = useAction();
  const [setup, setSetup] = useState<any>(null);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState({ current: '', next: '' });
  return (
    <div className="grid2">
      <div className="card stack">
        <h2>Segundo factor (2FA)</h2>
        {s.me?.user.mfaEnabled ? <div className="alert ok">Activado. Se te pedirá un código al entrar.</div> : (
          <>
            <p>Muy recomendable, y obligatorio para las funciones que el servidor tenga configuradas (por ejemplo, administrador, asesoría y tesorería). Con él activado, Conforme te pide el código al entrar y para aprobar pagos o verificar cuentas bancarias. Usa una aplicación como Google Authenticator, Microsoft Authenticator o 1Password.</p>
            {!setup ? <div><button className="primary" disabled={busy} onClick={async () => setSetup(await run(() => api.post('/auth/mfa/setup')))}>Activar</button></div> : (
              <>
                <p>Añade esta clave en tu aplicación (o abre el enlace en el móvil):</p>
                <code style={{ wordBreak: 'break-all' }}>{setup.secret}</code>
                <a href={setup.otpauth} className="hint">Abrir en la aplicación de autenticación</a>
                <Field label="Código de 6 dígitos"><input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></Field>
                <div><button className="primary" disabled={busy || code.length !== 6} onClick={async () => { if (await run(() => api.post('/auth/mfa/enable', { code }), '2FA activado')) { setSetup(null); s.refresh(); } }}>Confirmar</button></div>
              </>
            )}
          </>
        )}
      </div>
      <div className="card stack">
        <h2>Cambiar contraseña</h2>
        <Field label="Contraseña actual"><input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></Field>
        <Field label="Nueva contraseña" hint="Mínimo 10 caracteres. Se cerrarán tus otras sesiones."><input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
        <div><button disabled={busy || pw.next.length < 10} onClick={async () => { if (await run(() => api.post('/auth/password', pw), 'Contraseña cambiada')) setPw({ current: '', next: '' }); }}>Cambiar</button></div>
      </div>
    </div>
  );
}

function Plan() {
  const s = useSession();
  const { busy, run } = useAction();
  const { data, reload } = useLoad(() => api.get('/usage'), []);
  if (!data) return <Loading />;
  const plans = data.plans as Record<string, any>;
  const modules = data.moduleCatalog as Record<string, any>;
  const setOrg = async (body: any) => { if (await run(() => api.patch('/organization', body), 'Plan actualizado')) { reload(); s.refresh(); } };
  return (
    <div className="stack">
      <div className="grid4">
        <div className="card kpi"><span className="v">{data.invoices}</span><span className="l">Facturas este mes ({data.month})</span></div>
        <div className="card kpi"><span className="v">{data.included}</span><span className="l">Incluidas en el plan</span></div>
        <div className="card kpi"><span className="v">{data.overage}</span><span className="l">Excedente</span></div>
        <div className="card kpi"><span className="v">{eur(data.estimatedEur)}</span><span className="l">Cuota estimada del mes (sin IVA)</span></div>
      </div>
      <div className="grid3">
        {Object.entries(plans).map(([id, p]) => (
          <div key={id} className="card stack" style={{ borderColor: data.plan === id ? 'var(--accent)' : undefined }}>
            <h2>{p.name} {data.plan === id && <Badge tone="ok">Actual</Badge>}</h2>
            <div className="kpi"><span className="v">{eur(p.monthlyEur)}</span><span className="l">{p.perCompany ? 'por empresa y mes (mínimo 5)' : 'al mes'} · sin IVA</span></div>
            <ul className="hint" style={{ paddingLeft: 18, margin: 0 }}>
              <li>{p.includedInvoices} facturas/mes{p.perCompany ? ' por empresa' : ''} · excedente {eur(p.overageEur)}/factura</li>
              <li>{p.maxCompanies > 100 ? 'Empresas ilimitadas' : `${p.maxCompanies} empresa(s)`}</li>
              <li>{p.multilevelApproval ? 'Aprobación en varios niveles' : 'Aprobación de un nivel'}</li>
              <li>{p.einvoiceStatus ? 'Estados de factura electrónica' : 'Sin gestión de estados legales'}</li>
              <li>{p.api ? 'API y webhooks' : 'Sin API'}</li>
            </ul>
            {data.plan !== id && <button disabled={busy} onClick={() => setOrg({ plan: id })}>Cambiar a {p.name}</button>}
          </div>
        ))}
      </div>
      <div className="card stack">
        <h2>Módulos</h2>
        {Object.entries(modules).map(([id, m]) => (
          <label key={id} className="check"><input type="checkbox" checked={data.modules.includes(id)} onChange={(e) => setOrg({ modules: e.target.checked ? [...data.modules, id] : data.modules.filter((x: string) => x !== id) })} />
            <strong>{m.name}</strong> — {eur(m.monthlyEur)}/mes ({eur(m.perCompanyEur)} por empresa en el plan Despacho)</label>
        ))}
      </div>
      <p className="hint">Coste de lectura con IA este mes: {eur(data.aiCostEur)}.</p>
      {s.me?.role === 'admin' && <Cancellation />}
    </div>
  );
}

/** Baja del servicio con preaviso: solo la ve el administrador de la organización. */
function Cancellation() {
  const { busy, run } = useAction();
  const { data, reload } = useLoad(() => api.get('/organization/cancellation'), []);
  const [confirm, setConfirm] = useState(false);
  if (!data) return null;
  const effective = data.effectiveAt ? new Date(data.effectiveAt).toLocaleDateString('es-ES') : null;
  return (
    <div className="card stack">
      <h2>Baja del servicio</h2>
      {effective ? (
        <>
          <div className="alert warn">Baja solicitada{data.requestedBy ? ` por ${data.requestedBy}` : ''} el {dateTime(data.requestedAt)}. Será efectiva el <strong>{effective}</strong>: hasta entonces todo funciona con normalidad y puedes descargar tus datos; desde esa fecha se bloqueará el acceso.</div>
          <div><button disabled={busy} onClick={async () => { if (await run(() => api.del('/organization/cancellation'), 'Solicitud de baja cancelada')) reload(); }}>Cancelar la solicitud de baja</button></div>
        </>
      ) : (
        <>
          <p className="hint">Puedes darte de baja en cualquier momento con un preaviso de {data.noticeDays} días. Hasta la fecha efectiva podrás seguir usando Conforme y descargar tus datos. Qué pasa después con ellos se explica en el <a href="/legal/encargado" target="_blank" rel="noreferrer">contrato de encargado del tratamiento</a>.</p>
          <div><button className="danger" disabled={busy} onClick={() => setConfirm(true)}>Solicitar la baja</button></div>
        </>
      )}
      {confirm && (
        <Modal title="Solicitar la baja" onClose={() => setConfirm(false)} footer={<button className="danger" disabled={busy} onClick={async () => { if (await run(() => api.post('/organization/cancellation'), 'Baja solicitada')) { setConfirm(false); reload(); } }}>Sí, solicitar la baja</button>}>
          <p>La baja será efectiva dentro de {data.noticeDays} días. Hasta entonces todo seguirá funcionando y podrás cancelar la solicitud desde aquí. Te enviaremos un correo de confirmación.</p>
        </Modal>
      )}
    </div>
  );
}

function Companies() {
  const s = useSession();
  const { busy, run } = useAction();
  const { data, reload } = useLoad(() => api.get('/companies'), []);
  const [f, setF] = useState<any>(null);
  return (
    <div className="stack">
      <div className="table-wrap"><table>
        <thead><tr><th>Empresa</th><th>NIF</th><th>Programa</th><th>Buzón</th></tr></thead>
        <tbody>{(data ?? []).map((c: any) => <tr key={c.id}><td>{c.name}</td><td className="mono">{c.nif}</td><td>{c.accounting_system}</td><td className="mono">{c.inboxAddress}</td></tr>)}</tbody>
      </table></div>
      <div><button onClick={() => setF({ name: '', nif: '', taxRegime: 'IVA', accountingSystem: 'a3' })}>Añadir empresa</button></div>
      {f && (
        <Modal title="Nueva empresa" onClose={() => setF(null)} footer={<button className="primary" disabled={busy || !f.name || !f.nif} onClick={async () => { if (await run(() => api.post('/companies', f), 'Empresa creada')) { setF(null); reload(); s.refresh(); } }}>Crear</button>}>
          <Field label="Razón social"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="NIF"><input value={f.nif} onChange={(e) => setF({ ...f, nif: e.target.value })} /></Field>
          <Field label="Territorio"><select value={f.taxRegime} onChange={(e) => setF({ ...f, taxRegime: e.target.value })}><option value="IVA">IVA</option><option value="IGIC">IGIC (Canarias)</option><option value="IPSI">IPSI</option></select></Field>
          <Field label="Programa contable"><select value={f.accountingSystem} onChange={(e) => setF({ ...f, accountingSystem: e.target.value })}>{['a3', 'sage', 'contasol', 'holded', 'odoo', 'otro'].map((x) => <option key={x}>{x}</option>)}</select></Field>
        </Modal>
      )}
    </div>
  );
}

function ApiAccess() {
  const s = useSession();
  const { busy, run } = useAction();
  const keys = useLoad(() => api.get('/api-keys'), []);
  const hooks = useLoad(() => api.get('/webhooks'), []);
  const [secret, setSecret] = useState<string | null>(null);
  const [hook, setHook] = useState<any>(null);
  return (
    <div className="stack">
      <ErrorBox error={keys.error} />
      {secret && <div className="alert warn" style={{ wordBreak: 'break-all' }}>Cópialo ahora, no se volverá a mostrar: <code>{secret}</code></div>}
      <div className="card stack">
        <h2>Claves de API</h2>
        <p className="hint">Autenticación: cabecera <code>Authorization: Bearer conforme_…</code>. Los mismos recursos que la web, bajo <code>/api</code> (p. ej. <code>POST /api/upload</code>, <code>GET /api/invoices</code>).</p>
        <table><tbody>{(keys.data ?? []).map((k: any) => <tr key={k.id}><td>{k.name}</td><td className="mono">{k.prefix}…</td><td>{k.scopes.join(', ')}</td><td>{k.revoked_at ? <Badge>Revocada</Badge> : <Badge tone="ok">Activa</Badge>}</td><td>{dateTime(k.last_used_at)}</td>
          <td>{!k.revoked_at && <button className="small ghost" onClick={async () => { if (await run(() => api.del(`/api-keys/${k.id}`))) keys.reload(); }}>Revocar</button>}</td></tr>)}</tbody></table>
        <div><button disabled={busy} onClick={async () => { const name = prompt('Nombre de la clave (p. ej. «Integración ERP»)'); if (name) { const r = await run(() => api.post('/api-keys', { name, companyId: s.companyId })); if (r) { setSecret(r.key); keys.reload(); } } }}>Crear clave</button></div>
      </div>
      <div className="card stack">
        <h2>Webhooks</h2>
        <p className="hint">Firmados con HMAC-SHA256: cabecera <code>X-Conforme-Signature: t=…,v1=…</code> sobre <code>{'{t}.{cuerpo}'}</code>.</p>
        <table><tbody>{(hooks.data?.hooks ?? []).map((h: any) => <tr key={h.id}><td className="mono">{h.url}</td><td>{h.events.join(', ')}</td><td><button className="small ghost" onClick={async () => { if (await run(() => api.del(`/webhooks/${h.id}`))) hooks.reload(); }}>Eliminar</button></td></tr>)}</tbody></table>
        <div><button onClick={() => setHook({ url: '', events: [] })}>Añadir webhook</button></div>
      </div>
      {hook && (
        <Modal title="Nuevo webhook" onClose={() => setHook(null)} footer={<button className="primary" disabled={busy || !hook.url || !hook.events.length} onClick={async () => { const r = await run(() => api.post('/webhooks', hook)); if (r) { setSecret(r.secret); setHook(null); hooks.reload(); } }}>Crear</button>}>
          <Field label="URL (HTTPS)"><input value={hook.url} onChange={(e) => setHook({ ...hook, url: e.target.value })} /></Field>
          {(hooks.data?.events ?? []).map((ev: string) => <label key={ev} className="check"><input type="checkbox" checked={hook.events.includes(ev)} onChange={(e) => setHook({ ...hook, events: e.target.checked ? [...hook.events, ev] : hook.events.filter((x: string) => x !== ev) })} /> <code>{ev}</code></label>)}
        </Modal>
      )}
    </div>
  );
}
